import { useEffect, useRef, useCallback, useReducer } from 'preact/hooks';
import { camelizeKeys } from 'humps';
import { buildAvailabilityApiUrl, indexByDate } from '../utils/availabilityServiceHelpers';
import Polling from '../../../utils/Polling';

/**
 * @typedef {Object} AvailabilityState
 * @property {Record<string, Object>} departure  Days indexed by `YYYY-MM-DD` for the outbound leg.
 * @property {Record<string, Object>} return     Days indexed by `YYYY-MM-DD` for the return leg.
 * @property {Object|null} meta                  Camelized `meta` from the latest API response.
 * @property {boolean} fetching                  True while a polling cycle is in flight.
 * @property {boolean} fetched                   True once the window has resolved at least once.
 */
const initialState = {
  departure: {},
  return: {},
  meta: null,
  fetching: false,
  fetched: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'POLL_START':
      return {
        ...state,
        fetching: true,
        fetched: false,
      };
    case 'POLL_RESET_SCOPE':
      return {
        ...state,
        departure: {},
        return: {},
        meta: null,
        fetched: false,
      };
    case 'POLL_UPDATE': {
      const { departure, return: ret, meta } = action.payload;
      return {
        ...state,
        departure: { ...state.departure, ...departure },
        return: { ...state.return, ...ret },
        meta: meta ?? state.meta,
        fetching: true,
      };
    }
    case 'POLL_DONE': {
      const { departure, return: ret, meta } = action.payload;
      return {
        ...state,
        departure: { ...state.departure, ...departure },
        return: { ...state.return, ...ret },
        meta: meta ?? state.meta,
        fetching: false,
        fetched: true,
      };
    }
    case 'POLL_DEGRADED':
      return {
        ...state,
        fetching: false,
        fetched: true,
      };
    default:
      return state;
  }
};

const MAX_ATTEMPTS = 6;
const DEFAULT_POLL_DELAY_MS = 5000;
const MIN_POLL_DELAY_MS = 2000;
const MAX_POLL_DELAY_MS = 30000;
const REQUEST_TIMEOUT_MS = 30000;

/**
 * Polls the `/search/calendar` availability endpoint for a route + date window
 * and exposes both legs' results in a stable, indexed-by-date shape.
 *
 * A single `round=true` request returns both `departure[]` and `return[]`, so
 * the hook issues exactly one request per route + window and indexes both legs
 * from the same payload. Switching the active leg (e.g. when the user picks an
 * outbound date) does not refetch. A scope change (origin/destination) clears
 * both legs before the next poll starts.
 *
 * Polling uses the `Polling` utility with a per-attempt timeout
 * ({@link REQUEST_TIMEOUT_MS}) and a maximum number of attempts
 * ({@link MAX_ATTEMPTS}). On a degraded run (network error, timeout, or
 * `completion_state !== 'complete'` after the last attempt) the hook stops
 * polling and surfaces whatever partial results it received.
 *
 * @param {Object}   params
 * @param {string}   params.origin            Origin slug (e.g. "cdmx"). Polling is skipped when falsy.
 * @param {string}   params.destination       Destination slug. Polling is skipped when falsy.
 * @param {string}   params.from              ISO date (YYYY-MM-DD) - inclusive start of the window.
 * @param {string}   params.to                ISO date (YYYY-MM-DD) - inclusive end of the window.
 * @param {boolean}  [params.enabled=true]    When false, the hook is inert (no fetches, no resets).
 * @param {Object}   [params.options={}]
 * @param {string}   params.options.sourceUrl Base URL of the search API; required to fetch.
 * @param {boolean}  [params.options.forceHttps=false] Force the API URL to https.
 *
 * @returns {{
 *   departure: Record<string, Object>,
 *   return: Record<string, Object>,
 *   meta: Object|null,
 *   fetching: boolean,
 *   fetched: boolean,
 * }} `departure`/`return` are indexed by `YYYY-MM-DD` and populated together
 *    from one response. `fetching` is true between `start()` and the next
 *    terminal event (done or degraded). `fetched` flips true the first time the
 *    window reaches a terminal state.
 */
const useCalendarAvailability = ({
  origin,
  destination,
  from,
  to,
  enabled = true,
  options = {},
}) => {
  const { sourceUrl, forceHttps = false } = options;

  const [state, dispatch] = useReducer(reducer, initialState);
  const cacheRef = useRef(new Map());
  const pollerRef = useRef(null);
  const scopeRef = useRef(null);

  const fetchAvailability = useCallback(
    async ({ origin: o, destination: d, from: f, to: t, round, signal }) => {
      const url = buildAvailabilityApiUrl({
        origin: o,
        destination: d,
        from: f,
        to: t,
        sourceUrl,
        forceHttps,
        round,
      });
      if (!url) return null;
      const response = await fetch(url, { signal });
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}`);
        error.status = response.status;
        // Transient failures retry; a permanent client error (4xx other than
        // 408/429) won't change on retry, so mark it non-retryable to degrade
        // immediately. Network errors and timeouts carry no flag → they retry.
        error.retryable =
          response.status >= 500 || response.status === 408 || response.status === 429;
        throw error;
      }
      return response.json();
    },
    [sourceUrl, forceHttps]
  );

  useEffect(() => {
    if (!enabled || !origin || !destination || !from || !to || !sourceUrl) return undefined;

    // sourceUrl/forceHttps change the request target, so they are part of the
    // scope and the cache key — otherwise a cache hit could reuse data fetched
    // from a different endpoint.
    const scopeKey = `${origin}|${destination}|${sourceUrl}|${forceHttps}`;
    if (scopeRef.current !== scopeKey) {
      scopeRef.current = scopeKey;
      dispatch({ type: 'POLL_RESET_SCOPE' });
    }

    const key = `${scopeKey}|${from}|${to}`;

    if (cacheRef.current.has(key)) {
      dispatch({ type: 'POLL_DONE', payload: cacheRef.current.get(key) });
      return undefined;
    }

    pollerRef.current?.stop();

    const buildPayload = (raw) => ({
      departure: indexByDate(raw?.departure),
      return: indexByDate(raw?.return),
      meta: raw?.meta ? camelizeKeys(raw.meta) : null,
    });

    const poller = new Polling({
      fetchFn: (signal) =>
        fetchAvailability({ origin, destination, from, to, round: true, signal }),
      isDone: (raw) => raw?.meta?.completion_state === 'complete',
      getDelay: (raw) => raw?.meta?.warmup?.poll_after_ms,
      maxAttempts: MAX_ATTEMPTS,
      defaultDelay: DEFAULT_POLL_DELAY_MS,
      minDelay: MIN_POLL_DELAY_MS,
      maxDelay: MAX_POLL_DELAY_MS,
      requestTimeoutMs: REQUEST_TIMEOUT_MS,
      onUpdate: (raw) => {
        dispatch({ type: 'POLL_UPDATE', payload: buildPayload(raw) });
      },
      onDone: (raw) => {
        const payload = buildPayload(raw);
        cacheRef.current.set(key, payload);
        dispatch({ type: 'POLL_DONE', payload });
      },
      onDegraded: (reason) => {
        if (reason === 'fetch-error' || reason === 'request-timeout') {
          console.error('[useCalendarAvailability] poll degraded:', reason);
        }
        dispatch({ type: 'POLL_DEGRADED' });
      },
    });

    pollerRef.current = poller;
    dispatch({ type: 'POLL_START' });
    poller.start();

    return () => {
      poller.stop();
      if (pollerRef.current === poller) {
        pollerRef.current = null;
      }
    };
  }, [origin, destination, from, to, enabled, sourceUrl, forceHttps, fetchAvailability]);

  return {
    departure: state.departure,
    return: state.return,
    meta: state.meta,
    fetching: state.fetching,
    fetched: state.fetched,
  };
};

export default useCalendarAvailability;
